/**
 * StepRail — a slim vertical gutter of the open rollout's golden-process
 * steps, spanning the full transcript column. Segments are measured
 * against the ACTUAL transcript rows (via turnEventRanges + the row
 * DOM), so each one starts and ends exactly on a turn boundary and the
 * first step begins at the system message. Outcome colored, olive ring
 * on the diagnosed failure step, 2px gaps so the splits read. Hover for
 * the step name, turns, and outcome; click selects that step in the
 * timeline. Deliberately no scrolling.
 */

import { useLayoutEffect, useRef, useState } from "react";

import {
  humanizeTag,
  outcomeFill,
  turnEventRanges,
} from "@/components/benchmarks/goldenProcess";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type {
  ProcessRollout,
  TaskProcessResponse,
  TrajectoryMessage,
} from "@/api/generated/model";

interface SegmentBox {
  top: number;
  height: number;
}

export function StepRail({
  process,
  rollout,
  messages,
  listRef,
  selectedStep,
  onSelectStep,
}: {
  process: TaskProcessResponse;
  rollout: ProcessRollout;
  messages: TrajectoryMessage[];
  /** Wrapper around TranscriptEvents; its first child holds the rows. */
  listRef: React.RefObject<HTMLDivElement | null>;
  selectedStep: number;
  onSelectStep: (index: number) => void;
}): React.JSX.Element | null {
  const railRef = useRef<HTMLDivElement>(null);
  const [boxes, setBoxes] = useState<SegmentBox[]>([]);

  // Measure each step run's pixel window from the real rows, and
  // re-measure whenever the list resizes (expanding a row changes
  // every offset below it). Layout effect, not effect: painting the
  // rail at stale offsets first would visibly jump.
  useLayoutEffect(() => {
    const list = listRef.current;
    const rail = railRef.current;
    if (list === null || rail === null) return;
    const measure = (): void => {
      const rows = list.firstElementChild?.children;
      if (rows === undefined || rows.length === 0) return;
      const ranges = turnEventRanges(messages);
      const railTop = rail.getBoundingClientRect().top;
      const next: SegmentBox[] = rollout.step_runs.map((run) => {
        const startRange = ranges[Math.max(run.turn_start - 1, 0)];
        const endRange = ranges[Math.min(run.turn_end, ranges.length) - 1];
        // Step one owns the preamble: its segment starts at the system
        // message, not at the first assistant reply. A step whose turn
        // window lies entirely past the open transcript gets no
        // segment; falling back to row 0 painted the whole gutter.
        const firstEvent = run.turn_start <= 1 ? 0 : startRange?.start;
        if (firstEvent === undefined) {
          return { top: 0, height: 0 };
        }
        const lastEvent = Math.min(
          endRange?.end ?? rows.length - 1,
          rows.length - 1,
        );
        const firstRow = rows[Math.min(firstEvent, rows.length - 1)];
        const lastRow = rows[Math.max(lastEvent, 0)];
        if (firstRow === undefined || lastRow === undefined) {
          return { top: 0, height: 0 };
        }
        const top = firstRow.getBoundingClientRect().top - railTop;
        const bottom = lastRow.getBoundingClientRect().bottom - railTop;
        // 1px inset per side keeps a visible seam between segments.
        return { top: top + 1, height: Math.max(bottom - top - 2, 2) };
      });
      setBoxes((current) =>
        JSON.stringify(current) === JSON.stringify(next) ? current : next,
      );
    };
    measure();
    const observer = new ResizeObserver(() => {
      requestAnimationFrame(measure);
    });
    observer.observe(list);
    return () => {
      observer.disconnect();
    };
  }, [listRef, messages, rollout]);

  if (rollout.step_runs.length === 0) return null;
  return (
    <div ref={railRef} className="relative w-5 flex-none self-stretch">
      {rollout.step_runs.map((run, runIndex) => {
        const box = boxes[runIndex];
        if (box === undefined || box.height === 0) return null;
        const index = process.steps.findIndex(
          (step) => step.id === run.step_id,
        );
        const isFailureStep = rollout.failure?.primary_step_id === run.step_id;
        const isSelected = index === selectedStep;
        return (
          <Tooltip
            key={`${run.step_id}-${run.turn_start.toString()}`}
            delayDuration={500}
          >
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={`Step ${(index + 1).toString()}: ${run.title}`}
                aria-current={isSelected ? "step" : undefined}
                onClick={() => {
                  if (index >= 0) onSelectStep(index);
                }}
                className={cn(
                  "absolute left-0 w-full rounded transition-opacity hover:opacity-100",
                  isSelected ? "opacity-100 ring-2 ring-foreground" : "opacity-60",
                  !isSelected && isFailureStep && "ring-1 ring-brand-neutral-olive",
                )}
                style={{
                  top: `${box.top.toString()}px`,
                  height: `${box.height.toString()}px`,
                  backgroundColor: outcomeFill(run.outcome),
                }}
              />
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-80">
              <p className="text-xs text-foreground">
                {index >= 0 ? `${(index + 1).toString()}. ` : ""}
                {run.title}
              </p>
              <p className="font-mono text-xs text-muted-foreground">
                turns {run.turn_start}-{run.turn_end} ·{" "}
                {humanizeTag(run.outcome)}
                {isFailureStep ? " · diagnosed failure" : ""}
              </p>
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}
